import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PulseDrill } from "./PulseDrill";

const tone = (r) => ((r.lost || 0) >= 0.5 ? "#D32F2F" : (r.grade || 0) > 0 ? "#D97706" : "#16A34A");

// "What to act on" — one plain line per signal with its real count, worst driver first.
// Each "See them" opens the exact conversations / requests behind that line.
export function PulseActions({ tenantId, name, rows, compact = false, limit = 0 }) {
  const [drill, setDrill] = useState(null);
  const all = rows || [];
  const live = all.filter((r) => r.count > 0);
  const clear = all.filter((r) => !r.count);
  const shown = limit ? live.slice(0, limit) : live;
  return (
    <section data-testid="pulse-actions" className={compact ? "border-t border-[#F3EFE9] px-4 sm:px-5 py-3.5" : "rounded-2xl border border-[#E8E2D9] bg-white px-5 py-4"}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8C827A]">What to act on · this month</span>
        {live.length > 0 && <span className="text-[11px] text-[#8C827A]" data-testid="pulse-actions-count">{live.length} of {all.length} signal{all.length === 1 ? "" : "s"}</span>}
      </div>
      {live.length === 0 ? (
        <p className="text-sm text-[#57534E]" data-testid="pulse-actions-empty">Nothing needs action this month — every measured signal is at or better than its healthy line.</p>
      ) : (
        <ul className="divide-y divide-[#F3EFE9]" data-testid="pulse-actions-list">
          {shown.map((r) => (
            <li key={r.key} data-testid={`pulse-act-row-${r.key}`} className="grid grid-cols-[10px_1fr_auto] gap-3 items-center py-2.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: tone(r) }} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#1C1917]" data-testid={`pulse-act-text-${r.key}`}>{r.text}</div>
                <div className="text-[11px] text-[#8C827A] leading-snug">
                  {r.label}{r.display && r.display !== "—" ? ` · ${r.display}` : ""}{(r.lost || 0) >= 0.5 ? <span className="font-bold text-[#B91C1C]"> · {Math.round(r.lost)} points off</span> : r.status === "insufficient" ? " · not scored yet" : " · within healthy"}
                  {!compact && <span className="hidden sm:inline"> — {r.hint}</span>}
                </div>
              </div>
              <button type="button" data-testid={`pulse-act-${r.key}`} onClick={() => setDrill(r.key)}
                className="inline-flex items-center gap-1 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#292524] transition-colors">
                See them <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {limit > 0 && live.length > limit && (
        <Link to="/dashboard/pulse" data-testid="pulse-actions-more" className="mt-2 inline-block text-xs font-bold text-[#57534E] hover:text-[#1C1917]">
          {live.length - limit} more on the full Pulse page →
        </Link>
      )}
      {!compact && clear.length > 0 && (
        <p className="text-[11px] text-[#8C827A] mt-2" data-testid="pulse-actions-clear">Nothing to act on for {clear.map((r) => r.label).join(", ")}.</p>
      )}
      <PulseDrill tenantId={tenantId} signal={drill} name={name} onClose={() => setDrill(null)} />
    </section>
  );
}
